// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { Button, Input, InputBox } from '@imprivata-cloud/components';
import { Form } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ContinueButton from '../../components/continueButton/ContinueButton';
import CopyButton from '../../components/copyButton/copyButton';
import { STEPS } from '../../setup/constants';
import useICCTokenMutation from '../../setup/hooks/useICCTokenMutation';
import { useNavigateToNextStep } from '../../setup/hooks/useNavigateToNextStep';
import useCreateClientCredential from '../../setup/hooks/useVpamTokenMutation';

import './IntegrationToken.less';
import { ClientCredentialClientType } from '../../api/types';

const FORM_FIELD = { ICC_TOKEN: 'iccToken' };

export enum TokenType {
  EAM = 'EAM',
  VPAM = 'VPAM',
  AIP = 'AIP',
}

const tokenTypeToClientCredentialClientType: Record<TokenType, ClientCredentialClientType | null> =
  {
    [TokenType.VPAM]: ClientCredentialClientType.PAS,
    [TokenType.AIP]: ClientCredentialClientType.AIP,
    [TokenType.EAM]: null,
  };

type IntegrationTokenProps = { type: TokenType; isSetup?: boolean };

const IntegrationToken = ({ type, isSetup = true }: IntegrationTokenProps) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [token, setToken] = useState<string>('');
  const [inputValue, setInputValue] = useState('');

  const TRANSLATION_ROOT = `common.integration-token.${type}`;

  const navigateToNextStep = useNavigateToNextStep();
  const clientCredentialMutation = useCreateClientCredential({
    setToken,
    isSetup,
    clientType:
      tokenTypeToClientCredentialClientType[type] === null
        ? ClientCredentialClientType.PAS
        : tokenTypeToClientCredentialClientType[type],
  });
  const iccTokenMutation = useICCTokenMutation({ setToken, isSetup });
  const createTokenMutation =
    type === TokenType.VPAM || type === TokenType.AIP ? clientCredentialMutation : iccTokenMutation;

  const onContinueOrSkip = () => {
    navigateToNextStep(STEPS.SETUP_COMPLETE);
  };

  return (
    <div
      aria-description={`${type}-integration-token-container`}
      className={`integration-token ${isSetup ? 'setup' : 'portal'}`}
    >
      <p className="subtitle-instructions">{t(`${TRANSLATION_ROOT}.subtitle`)}</p>

      {/* PAS integration ID input */}
      <Form form={form}>
        {/* Both forms are side by side on Integration page so we need to make sure ids are unique, etc. */}
        <Form.Item name={`${FORM_FIELD.ICC_TOKEN}_${type}`}>
          <InputBox
            data-testid={`${type}-integration-id`}
            style={{ maxWidth: '700px' }}
            label={t(`${TRANSLATION_ROOT}.input`)}
            size="large"
            autoFocus={isSetup}
            onChange={e => setInputValue(e.target.value || '')}
          />
        </Form.Item>

        {/* Copy token buttons */}
        <Form.Item className="form-buttons">
          <Button
            aria-label="create-token-button"
            className="create-button"
            type="default"
            onClick={() => {
              createTokenMutation.mutate(inputValue?.trim());
            }}
            disabled={!inputValue?.trim() || createTokenMutation.isPending}
          >
            {t(`${TRANSLATION_ROOT}.create-token`)}
          </Button>
          {!isSetup && (
            <Input.TextArea
              aria-label="token"
              autoSize={{ minRows: 3, maxRows: 6 }}
              disabled
              label={t(`${TRANSLATION_ROOT}.token-textarea-label`)}
              value={token}
            />
          )}
          <div className="copy-button-container">
            <CopyButton
              textToCopy={token}
              type="text"
              style={{ visibility: !isSetup || token ? 'visible' : 'hidden' }}
              disabled={!token}
              dynamicLabel={{
                initialLabel: t('common.integration-token.copy-token'),
                labelAfterClick: t('common.copied'),
                dynamicLabelDuration: 5000,
              }}
            />
          </div>
        </Form.Item>

        {type === TokenType.EAM && isSetup && <p>{t(`${TRANSLATION_ROOT}.text-2`)}</p>}

        {/* Continue & Skip button */}
        {isSetup && (
          <Form.Item>
            <ContinueButton
              aria-label="continue-button"
              onClick={onContinueOrSkip}
              disabled={!token}
              secondaryButton={{
                'aria-label': 'skip-button',
                label: t('common.skip'),
                onClick: onContinueOrSkip,
              }}
            >
              {t('common.continue-button')}
            </ContinueButton>
          </Form.Item>
        )}
      </Form>
    </div>
  );
};

export { IntegrationToken };
